import { firebaseAuth } from '../../auth/firebase';
import addSeconds from 'date-fns/addSeconds';
import { Enviroment } from '@shared/enums/EnvironmentNames';
import { requestCustomToken, RequestCustomTokenResponse } from '@modules/App/api/requestCustomToken';
import { signOut } from 'firebase/auth';
import { paths } from '@shared/enums/paths';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_REFRESH_TOKEN_KEY = 'authRefresh';
const AUTH_EXPIRE_KEY = 'authExpire';
const AUTH_EMAIL_KEY = 'authEmail';
const SESSION_EXPIRE_AT_KEY = 'sessionExpireAt';
const REQUESTED_PATH = 'requestedPath';

export const clearAuthLocalStorageData = (): void => {
  localStorage.removeItem(AUTH_EMAIL_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_EXPIRE_KEY);
  localStorage.removeItem(SESSION_EXPIRE_AT_KEY);
};

export const setSessionExpireAtFront = () =>
  setSessionExpireAt(new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString());

export const getSessionExpireAt = (): string | null => localStorage.getItem(SESSION_EXPIRE_AT_KEY);

export const setSessionExpireAt = (date: string): void => localStorage.setItem(SESSION_EXPIRE_AT_KEY, date);

export const getCustomToken = (): string | null => localStorage.getItem(AUTH_TOKEN_KEY);

export const setCustomToken = (token: string): void => localStorage.setItem(AUTH_TOKEN_KEY, token);

export const clearCustomToken = (): void => localStorage.removeItem(AUTH_TOKEN_KEY);

export const getCurrentToken = async (refreshToken?: boolean): Promise<string | null> => {
  const isProdEnv = process.env.REACT_APP_ENV === Enviroment.Production;
  const customAuth = getCustomAuth();

  let customToken;
  if ((refreshToken || isCustomTokenExpired(new Date(customAuth?.expiresIn || ''))) && customAuth?.email) {
    const updatedCustomAuth = await refreshCustomAuth();
    customToken = updatedCustomAuth.idToken;
  } else {
    customToken = getCustomToken();
  }

  const fbToken = (await firebaseAuth?.currentUser?.getIdToken(refreshToken)) ?? null;

  return isProdEnv ? fbToken : customToken ?? fbToken;
};

export const setCustomAuth = ({ data, email }: { data: RequestCustomTokenResponse; email: string }): void => {
  const now = new Date();
  const expireDate = addSeconds(now, +data.token.expiresIn);
  localStorage.setItem(AUTH_EMAIL_KEY, email);
  localStorage.setItem(AUTH_TOKEN_KEY, data.token.idToken);
  localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, data.token.refreshToken);
  localStorage.setItem(AUTH_EXPIRE_KEY, expireDate.toISOString());
  localStorage.setItem(SESSION_EXPIRE_AT_KEY, data.expiresAt);
};

export const getCustomAuth = (): { email: string | null; token: string | null; expiresIn: string | null } | null => {
  const isProdEnv = process.env.REACT_APP_ENV === Enviroment.Production;
  if (isProdEnv) {
    return null;
  }
  return {
    email: localStorage.getItem(AUTH_EMAIL_KEY),
    token: localStorage.getItem(AUTH_TOKEN_KEY),
    expiresIn: localStorage.getItem(AUTH_EXPIRE_KEY),
  };
};

export const refreshCustomAuth = async (): Promise<{
  idToken: string;
  refreshToken: string;
  expiresIn: string;
}> => {
  const email = localStorage.getItem(AUTH_EMAIL_KEY);
  const refreshedCustomAuth = await requestCustomToken({ email: email ?? '', device: navigator.userAgent });
  setCustomAuth({ data: refreshedCustomAuth, email: email ?? '' });

  return refreshedCustomAuth.token;
};

export const isCustomTokenExpired = (tokenExpireDate: Date): boolean => {
  return tokenExpireDate <= new Date();
};

export const signOutCustom = () => {
  if (firebaseAuth) {
    clearAuthLocalStorageData();
    signOut(firebaseAuth);
    window.location.href = paths.login;
  }
};

export enum AuthErrorMessage {
  SessionExpired = 'Session expired',
  InvalidSession = 'Invalid session',
  NoAuthHeader = 'No auth header',
  NoSessionIdCookie = 'No sessionId cookie',
}

export const checkIsSessionExpired = (message: string): boolean => {
  return message === AuthErrorMessage.SessionExpired || message === AuthErrorMessage.InvalidSession;
};

export const getAuthToken = async (): Promise<string | null> => {
  const customAuth = getCustomAuth();
  if (customAuth?.email) {
    return customAuth.token ?? null;
  }
  if (!firebaseAuth) return null;
  await firebaseAuth.authStateReady();
  const user = firebaseAuth.currentUser;
  return user ? user.getIdToken() : null;
};

export const setRequestedPath = async (path: string): Promise<void> => {
  localStorage.setItem(REQUESTED_PATH, path);
};

export const getRequestedPath = async (): Promise<string | null> => {
  return localStorage.getItem(REQUESTED_PATH) ?? null;
};
